import {
	Tuple,
	Tuple2,
}	from "ts-base/tuple";
import { Fn, Fn0, Fn2 }	from "ts-base/fn";
import { Is }			from "ts-base/is";
import { NotNull }		from "ts-base/notNull";
import { Monoid }		from "ts-base/monoid";
import { Semigroup }	from "ts-base/semigroup";
import { Equal }		from "ts-base/equal";
import { Predicate }	from "ts-base/predicate";
import { Nullable }		from "ts-base/nullable";
import { Nea }			from "ts-base/nea";
import { Either }		from "ts-base/either";
import { Validated }	from "ts-base/validated";
import { Sync }			from "ts-base/sync";
import { Async }		from "ts-base/async";
import { Zio }			from "ts-base/zio";

export type Maybe<T>	= Maybe.Some<T> | Maybe.None<T>;

export namespace Maybe {
	//------------------------------------------------------------------------------
	//## basics

	export type Value<T>	= T extends Maybe<infer U> ? U : never;

	// exhaustive type parameters are necessary to infer the value
	export type Some<T>		= { tag:"Some", value:T	};
	export type None<_T>	= { tag:"None"			};

	export const some	= <T>(value:T):Maybe<T> => ({
		tag:	"Some",
		value:	value,
	});

	export const none	= <T>():Maybe<T>	=> ({
		tag:	"None",
	});

	export const isSome	= <T>(self:Maybe<T>):self is Some<T>	=> self.tag === "Some";
	export const isNone	= <T>(self:Maybe<T>):self is None<T>	=> self.tag === "None";

	export const cata	= <T>(self:Maybe<T>) => <N, S>(none:()=>N, some:Fn<T, S>):N|S	=>
		isSome(self) ? some(self.value) : none();

	export type Visitor<T, N, S>	= {
		some:	Fn<T, N>,
		none:	Fn0<S>,
	};

	export const visit	= <T>(self:Maybe<T>) => <N, S>(visitor:Visitor<T, N, S>):N|S	=>
		cata(self)(visitor.none, visitor.some);

	//------------------------------------------------------------------------------
	//## construction

	export const when	= <T>(value:T) => (condition:boolean):Maybe<T>	=>
		condition ? some(value) : none();

	export const test	= <T>(value:T) => (pred:Predicate<T>):Maybe<T> =>
		pred(value) ? some(value) : none();

	export const testIs	= <T>(value:T) => <U extends T>(pred:Is<T, U>):Maybe<U> =>
		pred(value) ? some(value) : none();

	//------------------------------------------------------------------------------
	//## conversion

	export const fromNullable	= <T>(value:T|null):Maybe<T>	=>
		value !== null ? some(value) : none();

	export const toNullable	= <T>(self:Maybe<T>):T|null =>
		isSome(self) ? self.value : null;

	export const toArray	= <T>(self:Maybe<T>):Array<T>	=>
		isSome(self) ? [ self.value ] : [];

	export const toSet	= <T>(self:Maybe<T>):Set<T>	=>
		new Set(toArray(self));

	//------------------------------------------------------------------------------
	//## tests

	export const includes	= <T>(self:Maybe<T>) => (value:T):boolean	=>
		exists(self)(it => it === value);

	/** aka some, but some is something different here */
	export const exists	= <T>(self:Maybe<T>) => (pred:Predicate<T>):boolean =>
		isSome(self) ? pred(self.value) : false;

	export const forall	= <T>(self:Maybe<T>) => (pred:Predicate<T>):boolean =>
		isSome(self) ? pred(self.value) : true;

	// some is something different...
	// export const some	= exists;
	export const every	= forall;

	//------------------------------------------------------------------------------
	//## filtering

	export const filter	= <T>(self:Maybe<T>) => (pred:Predicate<T>):Maybe<T>	=>
		isNone(self) ? self : pred(self.value) ? self : none();

	export const filterNot	= <T>(self:Maybe<T>) => (pred:Predicate<T>):Maybe<T>	=>
		filter(self)(Predicate.not(pred));

	export const partition = <T>(self:Maybe<T>) => (pred:Predicate<T>):Tuple2<Maybe<T>, Maybe<T>> =>
		[
			filter(self)(pred),
			filterNot(self)(pred),
		];

	export const collapseNullable	= <T>(self:Maybe<T|null>):Maybe<T>	=>
		filterIs(self)(NotNull.is);

	export const collapseMapNullable = <S>(self:Maybe<S>) => <T>(func:Fn<S, T|null>):Maybe<T>	=>
		collapseNullable(map(self)(func));

	export const filterIs	= <T>(self:Maybe<T>) => <U extends T>(is:Is<T, U>):Maybe<U>	=>
		isSome(self) && is(self.value) ? some(self.value) : none();

	//------------------------------------------------------------------------------
	//## sideffect

	export const forEach	= <T>(self:Maybe<T>)	=> (handler:Fn<T, void>):void	=> {
		if (isSome(self)) {
			handler(self.value);
		}
	};

	//------------------------------------------------------------------------------
	//## alternative

	/** danger! */
	export const unsafeGet	= <T>(self:Maybe<T>):T	=> {
		if (isSome(self))	return self.value;
		else				throw new Error("unexpected None");
	};

	export const orElse	= <T>(a:Maybe<T>) => (b:Maybe<T>):Maybe<T>	=>
		isSome(a) ? a : b;

	export const getOr	= <T>(self:Maybe<T>) => <U>(empty:U):T|U =>
		isSome(self) ? self.value : empty;

	export const mapOr	= <S>(self:Maybe<S>) => <T>(func:Fn<S, T>) => (empty:T):T =>
		isSome(self) ? func(self.value) : empty;

	//------------------------------------------------------------------------------
	//## functor

	export const map	= <S>(self:Maybe<S>) => <T>(func:Fn<S, T>):Maybe<T>	=>
		isSome(self) ? some(func(self.value)) : self;

	export const tupleBy	= <T>(self:Maybe<T>) => <U>(func:Fn<T, U>):Maybe<Tuple2<T, U>>	=>
		map(self)(it => [ it, func(it) ]);

	//------------------------------------------------------------------------------
	//## applicative functor

	export const pure	= some;

	export const ap		= <S>(self:Maybe<S>) => <T>(func:Maybe<Fn<S, T>>):Maybe<T>	=>
		flatMap(func)(map(self));

	export const map2	= <S1, S2>(s1:Maybe<S1>, s2:Maybe<S2>) => <T>(func:Fn2<S1, S2, T>):Maybe<T> =>
		isSome(s1) && isSome(s2)
		?	some(func(s1.value, s2.value))
		:	none();

	export const tuple2	= <S, T>(t1:Maybe<S>, t2:Maybe<T>):Maybe<Tuple2<S, T>>	=>
		map2(t1, t2)(Tuple.ofN);


	export const tupleN	= <T1, T extends ReadonlyArray<Maybe<unknown>>>(arg:Maybe<T1>, ...args:T):Maybe<Tuple.Prepend<T1, ValueN<T>>>	=>
		mapN(arg, ...args)(Tuple.ofN);

	export const mapN	= <S1, S extends ReadonlyArray<Maybe<unknown>>>(arg:Maybe<S1>, ...args:S) => <T>(func:(arg:S1, ...args:ValueN<S>)=>T):Maybe<T>	=> {
		if (isNone(arg))	return none();
		const value	= arg.value;
		const values	= [];
		for (const item of args) {
			if (isNone(item))	return none();
			values.push(item.value);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return some(func(value, ...values as any));
	};

	type ValueN<T>	= { [P in keyof T]:Value<T[P]> };

	export type SelfRecord<T>	= {
		[K in keyof T]: Maybe<T[K]>
	};

	export const object	= <T>(input:SelfRecord<T>):Maybe<Readonly<T>>	=> {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const out:any	= {};
		for (const key in input) {
			const part	= input[key];
			if (isNone(part))	return part;
			out[key]	= part.value;
		}
		return some(out);
	};

	//------------------------------------------------------------------------------
	//## monad

	export const flatMap	= <S>(self:Maybe<S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<T>	=>
		isSome(self) ? func(self.value) : self;

	export const flatten	= <T>(self:Maybe<Maybe<T>>):Maybe<T>	=>
		flatMap(self)(Fn.identity);

	export const pureKleisli	= <S, T>(func:Fn<S, T>):Fn<S, Maybe<T>>	=>
		Fn.andThen(func, it => pure(it));

	export const predicateKleisli = <T>(test:Predicate<T>):Fn<T, Maybe<T>> =>
		input => test(input) ? pure(input) : none();

	export const functionKleisli	= <S, T>(func:Maybe<Fn<S, T>>):Fn<S, Maybe<T>>	=>
		input => map(func)(func => func(input));

	export const andThen	= <A, B, C>(ab:Fn<A, Maybe<B>>, bc:Fn<B, Maybe<C>>):Fn<A, Maybe<C>>	=>
		a => flatMap(ab(a))(bc);

	export const andThen3	= <A, B, C, D>(ab:Fn<A, Maybe<B>>, bc:Fn<B, Maybe<C>>, cd:Fn<C, Maybe<D>>):Fn<A, Maybe<D>>	=>
		andThen(ab, andThen(bc, cd));

	export const andThen4	= <A, B, C, D, E>(ab:Fn<A, Maybe<B>>, bc:Fn<B, Maybe<C>>, cd:Fn<C, Maybe<D>>, de:Fn<D, Maybe<E>>):Fn<A, Maybe<E>>	=>
		andThen(ab, andThen(bc, andThen(cd, de)));

	export const mapKleisli	= <S, T>(base:Fn<S, Maybe<T>>) => <U>(func:Fn<T, U>):Fn<S, Maybe<U>>	=>
		andThen(base, pureKleisli(func));

	export const contraMapKleisli	= <S, T>(base:Fn<S, Maybe<T>>) => <R>(func:Fn<R, S>):Fn<R, Maybe<T>>	=>
		andThen(pureKleisli(func), base);

	//-----------------------------------------------------------------------------
	//## traversal

	export const traverseKleisli	= <S>(self:Maybe<S>) => <C, T>(func:Fn<S, Fn<C, T>>):Fn<C, Maybe<T>>	=>
		(t:C) => isSome(self) ? some(func(self.value)(t)) : self;

	export const sequenceKleisli	= <C, T>(self:Maybe<Fn<C, T>>):Fn<C, Maybe<T>>	=>
		traverseKleisli(self)(Fn.identity);

	export const traverseArray	= <S>(self:Maybe<S>) => <T>(func:Fn<S, ReadonlyArray<T>>):Array<Maybe<T>>	=>
		isSome(self) ? func(self.value).map(some) : [ none() ];

	export const sequenceArray	= <T>(self:Maybe<Array<T>>):Array<Maybe<T>>	=>
		traverseArray(self)(Fn.identity);

	export const traverseNullable	= <S>(self:Maybe<S>) => <T>(func:Fn<S, Nullable<T>>):Nullable<Maybe<T>>	=>
		isSome(self) ? Nullable.then(func(self.value))(some) : none();

	export const sequenceNullable	= <T>(self:Maybe<T|null>):Maybe<T>|null	=>
		traverseNullable(self)(Fn.identity);

	export const traverseNea		= <S>(self:Maybe<S>) => <T>(func:Fn<S, Nea<T>>):Nea<Maybe<T>>	=>
		isSome(self) ? Nea.map(func(self.value))(some) : Nea.single(none());

	export const sequenceNea	= <T>(self:Maybe<Nea<T>>):Nea<Maybe<T>>	=>
		traverseNea(self)(Fn.identity);

	export const traverseMaybe	= <S>(self:Maybe<S>) => <T>(func:Fn<S, Maybe<T>>):Maybe<Maybe<T>>	=>
		isSome(self) ? map(func(self.value))(some) : some(none());

	export const sequenceMaybe	= <T>(self:Maybe<Maybe<T>>):Maybe<Maybe<T>>	=>
		traverseMaybe(self)(Fn.identity);

	export const traverseEither	= <S>(self:Maybe<S>) => <E, T>(func:Fn<S, Either<E, T>>):Either<E, Maybe<T>>	=>
		isSome(self) ? Either.map(func(self.value))(some) : Either.right(none());

	export const sequenceEither	= <E, T>(self:Maybe<Either<E, T>>):Either<E, Maybe<T>>	=>
		traverseEither(self)(Fn.identity);

	export const traverseValidated	= <S>(self:Maybe<S>) => <E, T>(func:Fn<S, Validated<E, T>>):Validated<E, Maybe<T>>	=>
		isSome(self) ? Validated.map(func(self.value))(some) : Validated.valid(none());

	export const sequenceValidated	= <E, T>(self:Maybe<Validated<E, T>>):Validated<E, Maybe<T>>	=>
		traverseValidated(self)(Fn.identity);

	export const traversePromise	= <S>(self:Maybe<S>) => <T>(func:Fn<S, Promise<T>>):Promise<Maybe<T>>	=>
		isSome(self) ? func(self.value).then(some) : Promise.resolve(none());

	export const sequencePromise	= <T>(self:Maybe<Promise<T>>):Promise<Maybe<T>>	=>
		traversePromise(self)(Fn.identity);

	export const traverseSync	= <S>(self:Maybe<S>) => <T>(func:Fn<S, Sync<T>>):Sync<Maybe<T>>	=>
		() => isSome(self) ? some(func(self.value)()) : self;

	export const sequenceSync	= <S>(self:Maybe<Sync<S>>):Sync<Maybe<S>>	=>
		traverseSync(self)(Fn.identity);

	export const traverseAsync	= <S>(self:Maybe<S>) => <T>(func:Fn<S, Async<T>>):Async<Maybe<T>>	=>
		()	=> traversePromise(self)(it => func(it)());

	export const sequenceAsync	= <S>(self:Maybe<Async<S>>):Async<Maybe<S>>	=>
		traverseAsync(self)(Fn.identity);

	export const traverseZio	= <S>(self:Maybe<S>) => <C, T>(func:Fn<S, Zio<C, T>>):Zio<C, Maybe<T>>	=>
		(c:C)	=> traversePromise(self)((it) => func(it)(c));

	export const sequenceZio	= <C, T>(self:Maybe<Zio<C, T>>):Zio<C, Maybe<T>>	=>
		traverseZio(self)(Fn.identity);

	//-----------------------------------------------------------------------------
	//## monoid

	export const fold	= <T>(self:Maybe<T>) => (monoid:Monoid<T>):T	=>
		isSome(self) ? self.value : monoid.empty;

	export const foldMap	= <S>(self:Maybe<S>) => <T>(func:Fn<S, T>) => (monoid:Monoid<T>):T	=>
		isSome(self) ? func(self.value) : monoid.empty;

	//-----------------------------------------------------------------------------
	//## typeclasses

	export const equalOf	= <T>(base:Equal<T>):Equal<Maybe<T>>	=>
		(a, b) =>
		isNone(a)	&& isNone(b)	? true						:
		isSome(a)	&& isSome(b)	? base(a.value, b.value)	:
		false;

	export const equalOfBuiltin	= <T>():Equal<Maybe<T>>	=>
		equalOf(Equal.builtin);

	export const monoid	= <T>(base:Semigroup<T>):Monoid<Maybe<T>>	=> ({
		empty:	none(),
		concat:	(as, bs)	=>
			isSome(as) && isSome(bs)	? some(base.concat(as.value, bs.value))	:
			isSome(as)					? as	:
			isSome(bs)					? bs	:
			none(),
	});
}
